<template>
	<div class="shake-detect-page">
		<header class="shake-detect-header">
			<button type="button" class="shake-detect-back" @click="goBack">返回</button>
			<h1 class="shake-detect-title">摇一摇检测</h1>
			<p class="shake-detect-subtitle">与「摇享好礼」相同门槛，用于排查当前手机能否触发摇一摇抽奖</p>
		</header>

		<section class="shake-detect-card shake-detect-status" :class="statusClass">
			<p class="shake-detect-status-label">当前状态</p>
			<p class="shake-detect-status-text">{{ statusText }}</p>
			<p v-if="lastTriggerAt" class="shake-detect-status-extra">最近触发：{{ lastTriggerAt }}</p>
		</section>

		<section class="shake-detect-actions">
			<button
				type="button"
				class="shake-detect-btn shake-detect-btn-primary"
				:disabled="runtime.permissionRequesting"
				@click="requestPermission"
			>
				{{ permissionBtnText }}
			</button>
			<button type="button" class="shake-detect-btn" @click="resetSession">重置统计</button>
		</section>

		<section class="shake-detect-card">
			<h2 class="shake-detect-section-title">实时传感器</h2>
			<div class="shake-detect-grid">
				<div class="shake-detect-metric">
					<span class="label">监听中</span>
					<span class="value">{{ runtime.listenerActive ? '是' : '否' }}</span>
				</div>
				<div class="shake-detect-metric">
					<span class="label">权限</span>
					<span class="value">{{ permissionLabel }}</span>
				</div>
				<div class="shake-detect-metric">
					<span class="label">加速度来源</span>
					<span class="value">{{ runtime.current.accSource || '—' }}</span>
				</div>
				<div class="shake-detect-metric">
					<span class="label">采样次数</span>
					<span class="value">{{ runtime.sampleCount }}</span>
				</div>
				<div class="shake-detect-metric">
					<span class="label">x / y / z</span>
					<span class="value mono">{{ formatAxis(runtime.current) }}</span>
				</div>
				<div class="shake-detect-metric">
					<span class="label">当前 delta</span>
					<span class="value" :class="{ ok: runtime.deltaOk, warn: runtime.current.delta > 0 && !runtime.deltaOk }">
						{{ formatNum(runtime.current.delta) }}
						<small>/ {{ config.SHAKE_DELTA_THRESHOLD }}</small>
					</span>
				</div>
				<div class="shake-detect-metric">
					<span class="label">历史最大 delta</span>
					<span class="value">{{ formatNum(runtime.maxDelta) }}</span>
				</div>
				<div class="shake-detect-metric">
					<span class="label">有效峰值</span>
					<span class="value" :class="{ ok: runtime.peaksOk }">
						{{ runtime.peakCount }} / {{ config.SHAKE_PEAKS_REQUIRED }}
					</span>
				</div>
				<div class="shake-detect-metric">
					<span class="label">持续时长</span>
					<span class="value" :class="{ ok: runtime.durationOk }">
						{{ runtime.sessionDurationMs }}ms / {{ config.SHAKE_MIN_DURATION_MS }}ms
					</span>
				</div>
				<div class="shake-detect-metric">
					<span class="label">已达门槛</span>
					<span class="value" :class="{ ok: runtime.readyNow }">{{ runtime.readyNow ? '是' : '否' }}</span>
				</div>
				<div class="shake-detect-metric">
					<span class="label">模拟触发次数</span>
					<span class="value ok">{{ runtime.triggerCount }}</span>
				</div>
				<div class="shake-detect-metric">
					<span class="label">空加速度次数</span>
					<span class="value" :class="{ warn: runtime.nullAccelCount > 0 }">{{ runtime.nullAccelCount }}</span>
				</div>
			</div>
			<div class="shake-detect-meter">
				<div class="shake-detect-meter-bar" :style="{ width: deltaPercent + '%' }"></div>
			</div>
			<p class="shake-detect-meter-hint">delta 进度（阈值 {{ config.SHAKE_DELTA_THRESHOLD }}）</p>
		</section>

		<section class="shake-detect-card">
			<h2 class="shake-detect-section-title">门槛配置（与摇享好礼一致）</h2>
			<ul class="shake-detect-config-list">
				<li v-for="item in configItems" :key="item.key">
					<span>{{ item.label }}</span>
					<strong>{{ config[item.key] }}{{ item.unit }}</strong>
				</li>
			</ul>
		</section>

		<section v-if="diagnosis.issues.length" class="shake-detect-card shake-detect-alert">
			<h2 class="shake-detect-section-title">可能的问题</h2>
			<ul>
				<li v-for="(line, idx) in diagnosis.issues" :key="'i' + idx">{{ line }}</li>
			</ul>
		</section>

		<section v-if="diagnosis.hints.length" class="shake-detect-card shake-detect-hint">
			<h2 class="shake-detect-section-title">排查建议</h2>
			<ul>
				<li v-for="(line, idx) in diagnosis.hints" :key="'h' + idx">{{ line }}</li>
			</ul>
		</section>

		<section class="shake-detect-card">
			<h2 class="shake-detect-section-title">事件日志</h2>
			<ul v-if="runtime.logs.length" class="shake-detect-log">
				<li v-for="(log, idx) in runtime.logs" :key="idx">
					<span class="time">{{ log.time }}</span>
					<span class="type">{{ log.type }}</span>
					<span class="detail">{{ log.detail }}</span>
				</li>
			</ul>
			<p v-else class="shake-detect-empty">暂无日志，授权后开始监听并晃动手机</p>
		</section>

		<section class="shake-detect-card">
			<h2 class="shake-detect-section-title">设备与环境</h2>
			<div v-for="row in deviceRows" :key="row.key" class="shake-detect-info-row">
				<div class="info-label">{{ row.label }}</div>
				<div class="info-value mono">{{ row.value }}</div>
			</div>
		</section>
	</div>
</template>

<script>
import {
	SHAKE_MOTION_CONFIG_ITEMS,
	getShakeMotionConfigValues
} from './logic/shake-motion-config.js'
import {
	collectShakeDeviceInfo,
	createShakeDetectSession,
	diagnoseShakeEnvironment
} from './logic/shake-detect.js'

const PAGE_TITLE = '摇一摇检测'

export default {
	name: 'CarShakeDetect',
	data() {
		return {
			deviceInfo: collectShakeDeviceInfo(),
			config: getShakeMotionConfigValues(),
			configItems: SHAKE_MOTION_CONFIG_ITEMS,
			runtime: {
				permissionStatus: 'unknown',
				permissionRequesting: false,
				listenerActive: false,
				sampleCount: 0,
				nullAccelCount: 0,
				maxDelta: 0,
				triggerCount: 0,
				current: { x: 0, y: 0, z: 0, delta: 0, accSource: '' },
				peakCount: 0,
				peakTimes: [],
				sessionDurationMs: 0,
				durationOk: false,
				peaksOk: false,
				deltaOk: false,
				readyNow: false,
				logs: []
			},
			diagnosis: { issues: [], hints: [] },
			lastTriggerAt: '',
			session: null
		}
	},
	computed: {
		permissionLabel() {
			const map = {
				unknown: '未请求',
				granted: '已授权',
				denied: '已拒绝',
				unsupported: '不支持'
			}
			return map[this.runtime.permissionStatus] || this.runtime.permissionStatus
		},
		permissionBtnText() {
			if (this.runtime.permissionRequesting) return '请求授权中…'
			if (this.runtime.permissionStatus === 'granted') return '已授权（点击重新启用）'
			if (this.deviceInfo.motion && this.deviceInfo.motion.motionPermissionType === 'ios-requestPermission') {
				return '授权运动传感器（iOS）'
			}
			return '启用摇一摇监听'
		},
		deltaPercent() {
			const max = this.config.SHAKE_DELTA_THRESHOLD || 14
			const cur = Number(this.runtime.current.delta) || 0
			return Math.min(100, Math.round((cur / max) * 100))
		},
		statusClass() {
			if (this.runtime.triggerCount > 0) return 'is-ok'
			if (this.diagnosis.issues.length) return 'is-error'
			if (this.runtime.listenerActive) return 'is-warn'
			return 'is-idle'
		},
		statusText() {
			if (this.runtime.triggerCount > 0) {
				return '已通过门槛：本机可以触发摇一摇抽奖（共 ' + this.runtime.triggerCount + ' 次）'
			}
			if (!this.deviceInfo.motion.deviceMotionSupported) {
				return '不支持 DeviceMotionEvent，无法摇一摇'
			}
			if (this.runtime.permissionStatus === 'denied') {
				return '运动传感器权限被拒绝'
			}
			if (this.runtime.permissionStatus !== 'granted') {
				return '请先点击下方按钮授权/启用监听'
			}
			if (!this.runtime.listenerActive) {
				return '权限已获取，但尚未开始监听'
			}
			if (this.runtime.readyNow) {
				return '当前晃动已满足门槛，等待冷却后可再次触发'
			}
			return '监听中，请晃动手机…'
		},
		deviceRows() {
			const info = this.deviceInfo || {}
			const env = info.env || {}
			const rows = [
				{ key: 'ua', label: 'User-Agent', value: info.userAgent },
				{ key: 'platform', label: 'platform', value: info.platform },
				{ key: 'vendor', label: 'vendor', value: info.vendor },
				{ key: 'language', label: 'language', value: info.language },
				{ key: 'maxTouchPoints', label: 'maxTouchPoints', value: String(info.maxTouchPoints) },
				{ key: 'secure', label: 'isSecureContext', value: String(info.isSecureContext) },
				{ key: 'protocol', label: 'protocol', value: info.protocol },
				{ key: 'host', label: 'host', value: info.host },
				{
					key: 'screen',
					label: 'screen',
					value: info.screen
						? info.screen.width + '×' + info.screen.height + ' / ' + info.screen.orientation
						: '—'
				},
				{
					key: 'window',
					label: 'window',
					value: info.windowSize
						? info.windowSize.innerWidth +
							'×' +
							info.windowSize.innerHeight +
							' @' +
							info.windowSize.devicePixelRatio
						: '—'
				},
				{
					key: 'vv',
					label: 'visualViewport',
					value: info.visualViewport
						? info.visualViewport.width +
							'×' +
							info.visualViewport.height +
							' scale=' +
							info.visualViewport.scale
						: '—'
				},
				{ key: 'wechat', label: '微信内置浏览器', value: env.isWeChat ? '是' : '否' },
				{ key: 'ios', label: 'iOS', value: env.isIOS ? '是' : '否' },
				{ key: 'android', label: 'Android', value: env.isAndroid ? '是' : '否' },
				{ key: 'harmony', label: 'HarmonyOS', value: env.isHarmony ? '是' : '否' },
				{
					key: 'motionSupport',
					label: 'DeviceMotionEvent',
					value: info.motion.deviceMotionSupported ? '支持' : '不支持'
				},
				{
					key: 'motionPerm',
					label: '权限模式',
					value: info.motion.motionPermissionType
				},
				{
					key: 'orientSupport',
					label: 'DeviceOrientationEvent',
					value: info.motion.deviceOrientationSupported ? '支持' : '不支持'
				},
				{
					key: 'network',
					label: 'network',
					value: info.network
						? (info.network.effectiveType || '') +
							' downlink=' +
							info.network.downlink +
							' rtt=' +
							info.network.rtt
						: '—'
				},
				{ key: 'collectedAt', label: '采集时间', value: info.collectedAt }
			]
			return rows
		}
	},
	mounted() {
		document.title = PAGE_TITLE
		const self = this
		this.session = createShakeDetectSession({
			onUpdate: function (runtime) {
				self.runtime = runtime
				self.refreshDiagnosis()
			},
			onTrigger: function () {
				self.lastTriggerAt = new Date().toLocaleTimeString()
				self.refreshDiagnosis()
			},
			onLog: function (logs) {
				self.runtime = Object.assign({}, self.runtime, { logs: logs })
			}
		})
		this.refreshDiagnosis()
		const DM = window.DeviceMotionEvent
		if (DM && typeof DM.requestPermission !== 'function') {
			this.session.requestPermission()
		}
	},
	beforeDestroy() {
		if (this.session) this.session.destroy()
	},
	methods: {
		goBack() {
			if (window.history.length > 1) {
				this.$router.back()
			} else {
				this.$router.push('/carvue')
			}
		},
		requestPermission() {
			if (!this.session) return
			this.session.requestPermission().then(() => {
				this.runtime = this.session.getRuntime()
				this.refreshDiagnosis()
			})
		},
		resetSession() {
			if (this.session) {
				this.session.resetSession()
				this.runtime = this.session.getRuntime()
				this.refreshDiagnosis()
			}
		},
		refreshDiagnosis() {
			this.diagnosis = diagnoseShakeEnvironment(this.deviceInfo, this.runtime)
		},
		formatNum(n) {
			const v = Number(n)
			return Number.isFinite(v) ? v.toFixed(2) : '0.00'
		},
		formatAxis(cur) {
			if (!cur) return '0 / 0 / 0'
			return (
				this.formatNum(cur.x) +
				' / ' +
				this.formatNum(cur.y) +
				' / ' +
				this.formatNum(cur.z)
			)
		}
	}
}
</script>

<style scoped>
.shake-detect-page {
	min-height: 100vh;
	padding: 12px 12px 32px;
	box-sizing: border-box;
	background: #0f1115;
	color: #e8eaed;
	font-size: 14px;
	line-height: 1.5;
}
.shake-detect-header {
	margin-bottom: 12px;
}
.shake-detect-back {
	border: none;
	background: transparent;
	color: #8ab4f8;
	font-size: 14px;
	padding: 0;
	margin-bottom: 8px;
}
.shake-detect-title {
	margin: 0 0 4px;
	font-size: 20px;
	font-weight: 600;
}
.shake-detect-subtitle {
	margin: 0;
	color: #9aa0a6;
	font-size: 12px;
}
.shake-detect-card {
	background: #1a1d24;
	border-radius: 10px;
	padding: 12px;
	margin-bottom: 12px;
}
.shake-detect-section-title {
	margin: 0 0 10px;
	font-size: 15px;
	font-weight: 600;
}
.shake-detect-status-label {
	margin: 0 0 4px;
	font-size: 12px;
	color: #9aa0a6;
}
.shake-detect-status-text {
	margin: 0;
	font-size: 15px;
	font-weight: 600;
}
.shake-detect-status-extra {
	margin: 6px 0 0;
	font-size: 12px;
	color: #9aa0a6;
}
.shake-detect-status.is-ok {
	border: 1px solid #34a853;
}
.shake-detect-status.is-error {
	border: 1px solid #ea4335;
}
.shake-detect-status.is-warn {
	border: 1px solid #fbbc04;
}
.shake-detect-status.is-idle {
	border: 1px solid #3c4043;
}
.shake-detect-actions {
	display: flex;
	gap: 8px;
	margin-bottom: 12px;
}
.shake-detect-btn {
	flex: 1;
	border: 1px solid #3c4043;
	background: #252932;
	color: #e8eaed;
	border-radius: 8px;
	padding: 10px 8px;
	font-size: 14px;
}
.shake-detect-btn-primary {
	background: #1a73e8;
	border-color: #1a73e8;
	color: #fff;
}
.shake-detect-btn:disabled {
	opacity: 0.6;
}
.shake-detect-grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 8px;
}
.shake-detect-metric {
	background: #12151a;
	border-radius: 8px;
	padding: 8px;
}
.shake-detect-metric .label {
	display: block;
	font-size: 11px;
	color: #9aa0a6;
	margin-bottom: 4px;
}
.shake-detect-metric .value {
	font-size: 13px;
	font-weight: 600;
	word-break: break-all;
}
.shake-detect-metric .value small {
	font-weight: 400;
	color: #9aa0a6;
}
.shake-detect-metric .value.ok {
	color: #81c995;
}
.shake-detect-metric .value.warn {
	color: #f28b82;
}
.shake-detect-meter {
	margin-top: 10px;
	height: 8px;
	background: #12151a;
	border-radius: 4px;
	overflow: hidden;
}
.shake-detect-meter-bar {
	height: 100%;
	background: linear-gradient(90deg, #1a73e8, #34a853);
	transition: width 0.08s linear;
}
.shake-detect-meter-hint {
	margin: 6px 0 0;
	font-size: 11px;
	color: #9aa0a6;
}
.shake-detect-config-list {
	margin: 0;
	padding: 0;
	list-style: none;
}
.shake-detect-config-list li {
	display: flex;
	justify-content: space-between;
	padding: 6px 0;
	border-bottom: 1px solid #2a2f3a;
	font-size: 13px;
}
.shake-detect-config-list li:last-child {
	border-bottom: none;
}
.shake-detect-alert {
	border: 1px solid #ea4335;
}
.shake-detect-alert ul,
.shake-detect-hint ul {
	margin: 0;
	padding-left: 18px;
}
.shake-detect-hint {
	border: 1px solid #fbbc04;
}
.shake-detect-log {
	margin: 0;
	padding: 0;
	list-style: none;
	max-height: 220px;
	overflow: auto;
}
.shake-detect-log li {
	display: grid;
	grid-template-columns: 72px 56px 1fr;
	gap: 6px;
	padding: 6px 0;
	border-bottom: 1px solid #2a2f3a;
	font-size: 12px;
}
.shake-detect-log .time {
	color: #9aa0a6;
}
.shake-detect-log .type {
	color: #8ab4f8;
}
.shake-detect-empty {
	margin: 0;
	color: #9aa0a6;
	font-size: 13px;
}
.shake-detect-info-row {
	padding: 8px 0;
	border-bottom: 1px solid #2a2f3a;
}
.shake-detect-info-row:last-child {
	border-bottom: none;
}
.info-label {
	font-size: 11px;
	color: #9aa0a6;
	margin-bottom: 2px;
}
.info-value {
	font-size: 12px;
	word-break: break-all;
}
.mono {
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}
</style>
